import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { DataTable } from '../../components/tables/DataTable';
import { ROUTES } from '../../constants/routes';
import { getClient } from '../../services/clientService';
import { getProjects } from '../../services/projectService';
import { getSites } from '../../services/siteService';
import { getQuotes } from '../../services/quoteService';
import { getAllWorkOrders } from '../../services/workOrdersTabService';
import { listIntakes } from '../../services/intakeService';
import { getAllSamples } from '../../services/sampleService';
import type { Client, Site } from '../../types/project';
import type { ColumnDef } from '../projects/projectsColumns';
import { EditClientDialog } from './EditClientDialog';

const projectColumns: ColumnDef[] = [
  { field: 'ProjectId', headerName: 'Project ID', sortable: true, width: 120 },
  { field: 'ProjectName', headerName: 'Project Name', sortable: true, flex: 1.5 },
  { field: 'SiteName', headerName: 'Site', sortable: true, flex: 1 },
  { field: 'Status', headerName: 'Status', sortable: true, width: 100, type: 'status' },
  { field: 'sysLastModifiedDate', headerName: 'Last Modified', sortable: true, width: 140 },
];

const siteColumns: ColumnDef[] = [
  { field: 'SiteName', headerName: 'Site Name', sortable: true, flex: 1.5 },
  { field: 'SiteCity', headerName: 'City', sortable: true, flex: 1 },
  { field: 'projectCount', headerName: 'Projects', sortable: true, width: 80 },
];

// The client DIRECTORY (2026-08-04 audit): everything connected to this
// client in one place — a quote-stage job used to be INVISIBLE here because
// only projects were listed and a quote creates no project until it becomes
// a work order.
const quoteColumns: ColumnDef[] = [
  { sortable: false, field: 'QuoteNumber', headerName: 'Quote #', width: 130 },
  { sortable: false, field: 'Title', headerName: 'Title', flex: 1.5 },
  { sortable: false, field: 'Status', headerName: 'Status', width: 140, type: 'status' },
  { sortable: false, field: 'QuoteType', headerName: 'Type', width: 90 },
];

const workOrderColumns: ColumnDef[] = [
  { sortable: false, field: 'WorkOrderId', headerName: 'Work Order', width: 140 },
  { sortable: false, field: 'WorkOrderName', headerName: 'Name', flex: 1.5 },
  { sortable: false, field: 'ProjectId', headerName: 'Project', width: 120 },
  { sortable: false, field: 'WorkOrderStatus', headerName: 'Status', width: 120, type: 'status' },
  { sortable: false, field: 'SampleCount', headerName: 'Samples', width: 80 },
];

const intakeColumns: ColumnDef[] = [
  { sortable: false, field: 'ProjectName', headerName: 'Project', flex: 1.5 },
  { sortable: false, field: 'StatusLabel', headerName: 'Status', width: 170 },
  { sortable: false, field: 'ContactName', headerName: 'Contact', flex: 1 },
  { sortable: false, field: 'SampleCount', headerName: 'Samples', width: 80 },
  { sortable: false, field: 'Submitted', headerName: 'Submitted', width: 120 },
];

const sampleColumns: ColumnDef[] = [
  { sortable: false, field: 'SampleId', headerName: 'Sample', width: 140 },
  { sortable: false, field: 'ProjectId', headerName: 'Project', width: 120 },
  { sortable: false, field: 'LocationId', headerName: 'Location', width: 130 },
  { sortable: false, field: 'DepthTop', headerName: 'Depth Top', width: 90 },
  { sortable: false, field: 'SampleType', headerName: 'Type', width: 110 },
  { sortable: false, field: 'Status', headerName: 'Status', width: 120, type: 'status' },
];

const INTAKE_LABELS: Record<string, string> = {
  Draft: 'Draft (with requester)',
  Submitted: 'Submitted — review',
  Returned: 'Returned to requester',
  ConvertedQuote: 'Converted to quote',
  ConvertedWorkOrder: 'Converted to work order',
  Cancelled: 'Cancelled',
};

interface SiteRow { SiteKey: string; SiteName: string; SiteCity: string; projectCount: number; }
type Row = Record<string, unknown>;

export const ClientDetailPage = () => {
  const { clientKey } = useParams<{ clientKey: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [projects, setProjects] = useState<Row[]>([]);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [quotes, setQuotes] = useState<Row[]>([]);
  const [workOrders, setWorkOrders] = useState<Row[]>([]);
  const [intakes, setIntakes] = useState<Row[]>([]);
  const [samples, setSamples] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [sortBy, setSortBy] = useState('ProjectName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const fetchData = useCallback(async () => {
    if (!clientKey) return;
    setLoading(true);
    try {
      // Each list is independent — one failing endpoint must not blank the page.
      const [c, projResult, allSites, allQuotes, woResult, allIntakes, allSamples] = await Promise.all([
        getClient(clientKey),
        getProjects({ page: 0, pageSize: 10000 }),
        getSites().catch(() => [] as Site[]),
        getQuotes().catch(() => []),
        getAllWorkOrders({ clientKey, pageSize: 1000 }).catch(() => ({ data: [] })),
        listIntakes().catch(() => []),
        getAllSamples().catch(() => []),
      ]);
      setClient(c);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clientProjects = (projResult.data as any[]).filter((p) => p.ClientKey === clientKey);
      setProjects(clientProjects);
      const projectKeys = new Set(clientProjects.map((p) => String(p.ProjectKey).toUpperCase()));

      const siteKeysWithCounts = new Map<string, number>();
      for (const p of clientProjects) {
        const sk = p.SiteKey as string;
        if (sk) siteKeysWithCounts.set(sk, (siteKeysWithCounts.get(sk) || 0) + 1);
      }
      setSites(allSites
        .filter((s: Site) => siteKeysWithCounts.has(s.SiteKey))
        .map((s: Site) => ({
          SiteKey: s.SiteKey,
          SiteName: s.SiteName,
          SiteCity: s.SiteCity || '',
          projectCount: siteKeysWithCounts.get(s.SiteKey) || 0,
        })));

      setQuotes((allQuotes as Row[]).filter((q) => q.ClientKey === clientKey));
      setWorkOrders((woResult.data as unknown as Row[]) ?? []);
      setIntakes((allIntakes as Row[])
        .filter((r) => r.ClientKey === clientKey)
        .map((r) => ({
          ...r,
          StatusLabel: INTAKE_LABELS[String(r.Status)] ?? String(r.Status),
          Submitted: r.SubmittedAt ? String(r.SubmittedAt).slice(0, 10) : '—',
        })));
      setSamples((allSamples as unknown as Row[])
        .filter((s) => projectKeys.has(String(s.ProjectKey).toUpperCase())));
    } catch {
      setClient(null);
      setProjects([]);
      setSites([]);
      setQuotes([]);
      setWorkOrders([]);
      setIntakes([]);
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, [clientKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field: string) => {
    if (sortBy === field) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('asc'); }
  };

  const sorted = [...projects].sort((a, b) => {
    const aVal = (a as Row)[sortBy];
    const bVal = (b as Row)[sortBy];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const breadcrumbs = [
    { label: 'Clients', path: ROUTES.CLIENTS, entityType: 'client' as const },
    { label: client?.ClientName ?? 'Loading...', entityType: 'client' as const },
  ];

  const summary = [
    `${projects.length} project${projects.length === 1 ? '' : 's'}`,
    `${quotes.length} quote${quotes.length === 1 ? '' : 's'}`,
    `${workOrders.length} work order${workOrders.length === 1 ? '' : 's'}`,
    `${samples.length} sample${samples.length === 1 ? '' : 's'}`,
    ...(intakes.length > 0 ? [`${intakes.length} intake request${intakes.length === 1 ? '' : 's'}`] : []),
  ];

  const section = (title: string, count: number, node: ReactNode, alwaysShow = false) => (
    (alwaysShow || count > 0) && (
      <>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 4 }}>
          {title} ({count})
        </Typography>
        {node}
      </>
    )
  );

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      <Box sx={{ mb: 2 }}><Breadcrumb items={breadcrumbs} /></Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate(ROUTES.CLIENTS)}
          sx={{ textTransform: 'none', mr: 1 }}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '1.75rem', letterSpacing: '-0.025em' }}>
          {client?.ClientName ?? ''}
        </Typography>
        {client?.ClientStatus && (
          <Chip label={client.ClientStatus} size="small" color="success" variant="outlined" />
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<Edit />}
          onClick={() => setEditOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}>
          Edit Client
        </Button>
      </Box>

      {/* Everything connected to this client, at a glance. */}
      {!loading && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {summary.map((s) => <Chip key={s} label={s} size="small" variant="outlined" />)}
        </Box>
      )}

      {client && (client.ContactPerson || client.Email || client.Phone) && (
        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
          {client.ContactPerson && (
            <Typography variant="body2" color="text.secondary">Contact: {client.ContactPerson}</Typography>
          )}
          {client.Email && (
            <Typography variant="body2" color="text.secondary">Email: {client.Email}</Typography>
          )}
          {client.Phone && (
            <Typography variant="body2" color="text.secondary">Phone: {client.Phone}</Typography>
          )}
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 1 }}>
        Projects ({projects.length})
      </Typography>
      <DataTable
        columns={projectColumns}
        data={sorted}
        loading={loading}
        onRowClick={(row) => navigate(ROUTES.LOCATIONS.replace(':projectId', String(row.ProjectId)))}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />
      {!loading && projects.length === 0 && quotes.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          No projects yet — this client's work is still at the quote stage below. The project is
          created when a quote becomes a work order.
        </Typography>
      )}

      {section('Quotes', quotes.length, (
        <DataTable
          columns={quoteColumns}
          data={quotes}
          loading={loading}
          onRowClick={(row) => navigate(ROUTES.QUOTE_EDITOR.replace(':quoteKey', String(row.QuoteKey)))}
          sortBy="QuoteNumber"
          sortDir="desc"
        />
      ), true)}

      {section('Work Orders', workOrders.length, (
        <DataTable
          columns={workOrderColumns}
          data={workOrders}
          loading={loading}
          onRowClick={(row) => navigate(
            ROUTES.WORK_ORDER_DETAIL
              .replace(':projectId', String(row.ProjectId))
              .replace(':workOrderId', String(row.WorkOrderKey)), // route param is the KEY
          )}
          sortBy="WorkOrderId"
          sortDir="desc"
        />
      ), true)}

      {section('Intake Requests', intakes.length, (
        <DataTable
          columns={intakeColumns}
          data={intakes}
          loading={loading}
          onRowClick={(row) => navigate(ROUTES.INTAKE_DETAIL.replace(':intakeKey', String(row.IntakeRequestKey)))}
          sortBy="Submitted"
          sortDir="desc"
        />
      ))}

      {section('Samples', samples.length, (
        <DataTable
          columns={sampleColumns}
          data={samples}
          loading={loading}
          onRowClick={(row) => navigate(
            `${ROUTES.LOCATIONS.replace(':projectId', String(row.ProjectId))}?location=${String(row.LocationKey)}`,
          )}
          sortBy="SampleId"
          sortDir="asc"
        />
      ))}

      {section('Sites', sites.length, (
        <DataTable
          columns={siteColumns}
          data={sites as unknown as Row[]}
          loading={loading}
          onRowClick={(row) => navigate(ROUTES.SITE_DETAIL.replace(':siteKey', String(row.SiteKey)))}
          sortBy="SiteName"
          sortDir="asc"
        />
      ))}

      <EditClientDialog
        open={editOpen}
        client={client}
        onClose={() => setEditOpen(false)}
        onUpdated={() => { setEditOpen(false); fetchData(); }}
      />
    </Box>
  );
};

export default ClientDetailPage;
